import React, { useState, useEffect } from 'react';
import { useForm } from '../../../context/FormContext';
import FormField from '../../common/FormField/FormField';
import FileUpload from '../../common/FileUpload/FileUpload';
import { validateSection, isSectionComplete } from '../../../utils/validation';
import './SanctionsAudit.css';

const SanctionsAudit = ({ data, errors, onSave, isLoading, isReadOnly }) => {
  const { actions } = useForm();
  const [formData, setFormData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;

    const newData = { ...formData, [field]: value };
    setFormData(newData);
    setHasChanges(true);

    actions.updateSectionData('sanctionsAudit', newData, 'in_progress');
  };

  const handleSave = () => {
    if (hasChanges) {
      // Validate the section
      const validationErrors = validateSection('sanctionsAudit', formData);
      const isComplete = isSectionComplete('sanctionsAudit', formData);

      // Set validation errors in context
      actions.setSectionErrors('sanctionsAudit', validationErrors);

      // Determine status based on completion
      const status = isComplete ? 'completed' : 'in_progress';

      // Update section data with proper status
      actions.updateSectionData('sanctionsAudit', formData, status);

      // Call the save handler
      onSave(formData);
      setHasChanges(false);
    }
  };

  // Removed auto-saving - users must manually save sections

  return (
    <div className="sanctions-audit">
      <div className="section-intro">
        <p>Please provide information about sanctions and audit history.</p>
      </div>

      <FormField
        label="Has your company been sanctioned by its supervisory authority?"
        required
        error={errors.hasSanctions}
      >
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="hasSanctions"
              value="true"
              checked={formData.hasSanctions === true}
              onChange={() => handleChange('hasSanctions', true)}
              disabled={isReadOnly}
            />
            <span>Yes</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="hasSanctions"
              value="false"
              checked={formData.hasSanctions === false}
              onChange={() => handleChange('hasSanctions', false)}
              disabled={isReadOnly}
            />
            <span>No</span>
          </label>
        </div>
      </FormField>

      {formData.hasSanctions === true && (
        <FormField
          label="Sanction context"
          required
          error={errors.sanctionContext}
        >
          <textarea
            className={`form-textarea ${errors.sanctionContext ? 'error' : ''}`}
            value={formData.sanctionContext || ''}
            onChange={(e) => handleChange('sanctionContext', e.target.value)}
            placeholder="Please provide details about the sanctions"
            rows={4}
            disabled={isReadOnly}
          />
        </FormField>
      )}

      <FormField
        label="External audit status in last two years"
        required
        error={errors.hasAudit}
      >
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="hasAudit"
              value="true"
              checked={formData.hasAudit === true}
              onChange={() => handleChange('hasAudit', true)}
              disabled={isReadOnly}
            />
            <span>Yes</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="hasAudit"
              value="false"
              checked={formData.hasAudit === false}
              onChange={() => handleChange('hasAudit', false)}
              disabled={isReadOnly}
            />
            <span>No</span>
          </label>
        </div>
      </FormField>

      {formData.hasAudit === true && (
        <FormField
          label="Audit report upload"
          required
          error={errors.auditReport}
        >
          <FileUpload
            value={formData.auditReport}
            onChange={(file) => handleChange('auditReport', file)}
            error={errors.auditReport}
            disabled={isReadOnly}
            accept=".pdf,.doc,.docx"
            placeholder="Upload audit report"
            documentType="auditReport"
          />
        </FormField>
      )}

      {!isReadOnly && (
        <div className="section-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Section'}
          </button>

          {hasChanges && (
            <span className="unsaved-indicator">
              You have unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SanctionsAudit;
